import { Button, DropdownButton, Menu, Tooltip } from '@neo4j-ndl/react';
import { CommandLineIconOutline, ExploreIcon, RectangleGroupIconOutline } from '@neo4j-ndl/react/icons';
import { APP_SCOPE } from '@nx/constants';
import { type FleetManagementTypes, MODAL_TYPE, useModal } from '@nx/state';
import { URLs, isNotNullish, isNullish } from '@nx/stdlib';
import { useNxNavigate } from '@nx/ui';
import { useRef, useState } from 'react';

type ConnectMenuProps = {
  deployment: FleetManagementTypes.DeploymentDetails;
  isDisabled: boolean;
  type?: 'table' | 'list';
};

export const ConnectMenu = ({ deployment, type = 'list', isDisabled }: ConnectMenuProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const connectInstanceModal = useModal(MODAL_TYPE.CONNECTION_FORM);

  const nxNavigate = useNxNavigate();

  const handleClick = () => setIsMenuOpen((prevState) => !prevState);

  const handleConnect = (app: APP_SCOPE) => {
    if (isNullish(deployment.connectionUrl) || !URLs.Neo4jURL.isValid(deployment.connectionUrl)) {
      return;
    }
    const url = new URLs.Neo4jURL(deployment.connectionUrl);
    nxNavigate(app);
    connectInstanceModal.open({
      instanceDetails: {
        id: deployment.deploymentId,
        name: deployment.name,
      },
      initialValues: {
        username: 'neo4j',
        protocol: url.protocol,
        hostname: url.host,
      },
      source: { type: 'fleet-management', sourceId: deployment.deploymentId },
      error: undefined,
      warning: undefined,
      hideConnectionMethods: false,
    });
  };

  return (
    <>
      <Tooltip type="simple" isDisabled={isNotNullish(deployment.connectionUrl)}>
        <Tooltip.Trigger hasButtonWrapper>
          <DropdownButton
            ref={menuRef}
            isOpen={isMenuOpen}
            onClick={handleClick}
            size={type === 'list' ? 'medium' : 'small'}
            isDisabled={isNullish(deployment.connectionUrl)}
            htmlAttributes={{
              'aria-label': 'Connection menu',
            }}
          >
            Connect
          </DropdownButton>
        </Tooltip.Trigger>
        <Tooltip.Content>{isNullish(deployment.connectionUrl) && 'No connection URL available'}</Tooltip.Content>
      </Tooltip>
      <Menu isOpen={isMenuOpen} anchorRef={menuRef} onClose={handleClick}>
        <Menu.Items>
          <Menu.Item
            title="Query"
            icon={<CommandLineIconOutline />}
            isDisabled={isDisabled}
            onClick={() => {
              void handleConnect(APP_SCOPE.query);
            }}
          />
          <Menu.Item
            title="Explore"
            icon={<ExploreIcon className="n-size-token-7" />}
            isDisabled={isDisabled}
            onClick={() => {
              void handleConnect(APP_SCOPE.explore);
            }}
          />
          <Menu.Item
            title="Dashboards"
            icon={<RectangleGroupIconOutline />}
            isDisabled={isDisabled}
            onClick={() => {
              void handleConnect(APP_SCOPE.dashboards);
            }}
          />
        </Menu.Items>
      </Menu>
    </>
  );
};

export const ConnectButton = ({
  deployment,
  isDisabled,
}: {
  deployment: FleetManagementTypes.DeploymentDetails;
  isDisabled: boolean;
}) => {
  const connectInstanceModal = useModal(MODAL_TYPE.CONNECTION_FORM);

  const handleConnect = () => {
    if (isNullish(deployment.connectionUrl) || !URLs.Neo4jURL.isValid(deployment.connectionUrl)) {
      return;
    }
    const url = new URLs.Neo4jURL(deployment.connectionUrl);
    connectInstanceModal.open({
      instanceDetails: {
        id: deployment.deploymentId,
        name: deployment.name,
      },
      initialValues: {
        username: 'neo4j',
        protocol: url.protocol,
        hostname: url.host,
      },
      source: { type: 'fleet-management', sourceId: deployment.deploymentId },
      error: undefined,
      warning: undefined,
      hideConnectionMethods: false,
    });
  };

  return (
    <Tooltip type="simple" isDisabled={!isDisabled} isPortaled={false}>
      <Tooltip.Trigger hasButtonWrapper>
        <Button fill="filled" size="medium" color="primary" isDisabled={isDisabled} onClick={handleConnect}>
          Connect
        </Button>
      </Tooltip.Trigger>
      <Tooltip.Content>No connection URL configured for this deployment</Tooltip.Content>
    </Tooltip>
  );
};
